import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import type { ExplainItem } from '../explain-table/explain-table.model';
import { ExplainTableComponent } from '../explain-table/explain-table.component';
import type { MeasureId } from '../measures/measures.model';
import type { MeasureValues } from '../measures/measures.util';
import { computeAllMeasures } from '../measures/measures.util';
import { OgiveChartComponent } from './ogive-chart.component';
import type { OgiveDisplayConfig, OgiveParams } from './ogive-chart.model';
import { computeOgive, inverseInterpolateOgive } from './ogive-chart.util';

// ---------------------------------------------------------------------------
// Element definitions
// ---------------------------------------------------------------------------

type OgiveElementId = 'q1' | 'median' | 'q3' | 'iqr';

interface OgiveElementDef {
  readonly id: OgiveElementId;
  readonly label: string;
  readonly description: string;
  readonly color: string;
}

const ELEMENT_DEFS: readonly OgiveElementDef[] = [
  {
    id: 'q1',
    label: 'Lower Quartile (Q\u2081)',
    description: 'Read across from 25% of total frequency',
    color: '#8b5cf6',
  },
  { id: 'median', label: 'Median', description: 'Read across from 50% of total frequency', color: '#14b8a6' },
  {
    id: 'q3',
    label: 'Upper Quartile (Q\u2083)',
    description: 'Read across from 75% of total frequency',
    color: '#8b5cf6',
  },
  {
    id: 'iqr',
    label: 'Interquartile Range (IQR)',
    description: 'Q\u2083 \u2212 Q\u2081, the spread of the middle 50%',
    color: '#8b5cf6',
  },
];

// ---------------------------------------------------------------------------
// Element → activeMeasures mapping
// ---------------------------------------------------------------------------

const ELEMENT_TO_MEASURES: Readonly<Record<OgiveElementId, ReadonlySet<MeasureId>>> = {
  q1: new Set(['q1']),
  median: new Set(['median']),
  q3: new Set(['q3']),
  iqr: new Set(['iqr']),
};

const ALL_PERCENTILE_MEASURES: ReadonlySet<MeasureId> = new Set(['q1', 'median', 'q3', 'iqr']);
const EMPTY_MEASURES: ReadonlySet<MeasureId> = new Set();
const EMPTY_VALUES: MeasureValues = new Map();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(1)).toString();
}

function getElementValue(id: OgiveElementId, q1: number, median: number, q3: number, iqr: number): string {
  switch (id) {
    case 'q1':
      return `\u2248 ${fmt(q1)}`;
    case 'median':
      return `\u2248 ${fmt(median)}`;
    case 'q3':
      return `\u2248 ${fmt(q3)}`;
    case 'iqr':
      return `\u2248 ${fmt(q3)} \u2212 ${fmt(q1)} = ${fmt(iqr)}`;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-ogive-explain',
  standalone: true,
  imports: [OgiveChartComponent, ExplainTableComponent],
  template: `
    <div [class]="explain() ? 'ogive-container ogive-container--explain' : 'ogive-container'">
      <dojo-ogive-chart
        [params]="params()"
        [display]="display()"
        [showPercentage]="percentage()"
        [activeMeasures]="activeMeasures()"
        [measureValues]="measureValues()"
      />
    </div>
    @if (explain() && explainItems().length > 0) {
      <dojo-explain-table
        [items]="explainItems()"
        [(highlightedId)]="highlightedId"
        headerLabel="Measure"
        headerDescription="How to read it"
      />
    }
  `,
  styles: `
    .ogive-container {
      width: 100%;
      height: 100%;
    }

    .ogive-container--explain {
      height: 320px;
    }
  `,
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OgiveExplainComponent {
  readonly params = input.required<OgiveParams>();
  readonly display = input<OgiveDisplayConfig>({});
  readonly explain = input<boolean>(false);
  readonly percentage = input<boolean>(false);

  readonly highlightedId = signal<string | null>(null);

  readonly activeMeasures = computed<ReadonlySet<MeasureId>>(() => {
    if (!this.explain()) {
      return EMPTY_MEASURES;
    }
    const el = this.highlightedId() as OgiveElementId | null;
    if (el === null) {
      return ALL_PERCENTILE_MEASURES;
    }
    return ELEMENT_TO_MEASURES[el] ?? EMPTY_MEASURES;
  });

  readonly measureValues = computed<MeasureValues>(() => {
    if (!this.explain()) {
      return EMPTY_VALUES;
    }
    return computeAllMeasures(this.params().data);
  });

  readonly explainItems = computed<ExplainItem[]>(() => {
    const data = this.params().data;
    if (data.length === 0) {
      return [];
    }

    const ogive = computeOgive(data, this.params().config, this.percentage());
    if (ogive.points.length < 2 || ogive.yMax <= 0) {
      return [];
    }

    const q1x = inverseInterpolateOgive(ogive.points, ogive.yMax * 0.25);
    const medianX = inverseInterpolateOgive(ogive.points, ogive.yMax * 0.5);
    const q3x = inverseInterpolateOgive(ogive.points, ogive.yMax * 0.75);

    if (q1x === null || medianX === null || q3x === null) {
      return [];
    }

    const iqr = q3x - q1x;

    return ELEMENT_DEFS.map((def) => ({
      id: def.id,
      label: def.label,
      description: def.description,
      color: def.color,
      value: getElementValue(def.id, q1x, medianX, q3x, iqr),
    }));
  });
}
